import type { ReactElement } from 'react';
import { useCallback, useEffect, useState } from 'react';
import type { JukeboxConfigResponse, JukeboxTrackSummary } from '@fetmode/types';
import { ApiError } from '@fetmode/api-client';
import { useAuth } from '@fetmode/app-core';
import { t } from '@fetmode/i18n';
import { Button } from '@fetmode/ui';

type TrackDraft = {
  title: string;
  artist: string;
  durationSec: string;
};

const emptyDraft = (): TrackDraft => ({
  title: '',
  artist: '',
  durationSec: '180',
});

export function ModelJukeboxCard(): ReactElement {
  const { api } = useAuth();
  const [config, setConfig] = useState<JukeboxConfigResponse | null>(null);
  const [draft, setDraft] = useState<TrackDraft>(emptyDraft);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async (): Promise<void> => {
    const result = await api.getMyJukebox();
    setConfig(result);
  }, [api]);

  useEffect(() => {
    void load()
      .catch(() => {
        setError(t('common.studio.jukebox.loadError'));
      })
      .finally(() => {
        setIsLoading(false);
      });
  }, [load]);

  async function saveSettings(
    patch: Partial<{ enabled: boolean; requestCoins: number; bumpCoins: number }>,
  ): Promise<void> {
    setIsSaving(true);
    setError(null);
    try {
      const result = await api.updateMyJukebox(patch);
      setConfig(result);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : t('common.studio.jukebox.saveError'));
    } finally {
      setIsSaving(false);
    }
  }

  async function handleAdd(): Promise<void> {
    const durationSec = Number.parseInt(draft.durationSec, 10);
    if (!draft.title.trim() || !draft.artist.trim()) {
      setError(t('common.studio.jukebox.invalidTrack'));
      return;
    }
    if (!Number.isFinite(durationSec) || durationSec < 1) {
      setError(t('common.studio.jukebox.invalidTrack'));
      return;
    }
    setIsSaving(true);
    setError(null);
    try {
      await api.createJukeboxTrack({
        title: draft.title.trim(),
        artist: draft.artist.trim(),
        durationSec,
      });
      setDraft(emptyDraft());
      await load();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : t('common.studio.jukebox.saveError'));
    } finally {
      setIsSaving(false);
    }
  }

  async function handleToggleEnabled(track: JukeboxTrackSummary): Promise<void> {
    setIsSaving(true);
    setError(null);
    try {
      await api.updateJukeboxTrack(track.id, { enabled: !track.enabled });
      await load();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : t('common.studio.jukebox.saveError'));
    } finally {
      setIsSaving(false);
    }
  }

  async function handleDelete(trackId: string): Promise<void> {
    setIsSaving(true);
    setError(null);
    try {
      await api.deleteJukeboxTrack(trackId);
      await load();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : t('common.studio.jukebox.saveError'));
    } finally {
      setIsSaving(false);
    }
  }

  if (isLoading) {
    return <p style={{ color: 'var(--fm-color-text-muted)' }}>{t('common.studio.jukebox.loading')}</p>;
  }

  if (!config) {
    return <p className="fm-error">{t('common.studio.jukebox.loadError')}</p>;
  }

  return (
    <div className="fm-card">
      <h2 style={{ marginTop: 0, fontSize: '1rem' }}>{t('common.studio.jukebox.title')}</h2>
      <p style={{ margin: '0 0 1rem', color: 'var(--fm-color-text-muted)', fontSize: '0.875rem' }}>
        {t('common.studio.jukebox.hint')}
      </p>

      {error ? <p className="fm-error">{error}</p> : null}

      <div style={{ display: 'grid', gap: '0.75rem', marginBottom: '1rem' }}>
        <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
          <input
            type="checkbox"
            checked={config.settings.enabled}
            disabled={isSaving}
            onChange={(event) => {
              void saveSettings({ enabled: event.target.checked });
            }}
          />
          {t('common.studio.jukebox.enabled')}
        </label>
        <label>
          {t('common.studio.jukebox.requestCoins')}
          <input
            className="fm-input"
            type="number"
            min={1}
            value={config.settings.requestCoins}
            disabled={isSaving}
            onChange={(event) => {
              const next = Number.parseInt(event.target.value, 10);
              if (Number.isFinite(next) && next >= 1) {
                void saveSettings({ requestCoins: next });
              }
            }}
            style={{ display: 'block', width: '100%', marginTop: '0.25rem' }}
          />
        </label>
        <label>
          {t('common.studio.jukebox.bumpCoins')}
          <input
            className="fm-input"
            type="number"
            min={1}
            value={config.settings.bumpCoins}
            disabled={isSaving}
            onChange={(event) => {
              const next = Number.parseInt(event.target.value, 10);
              if (Number.isFinite(next) && next >= 1) {
                void saveSettings({ bumpCoins: next });
              }
            }}
            style={{ display: 'block', width: '100%', marginTop: '0.25rem' }}
          />
        </label>
      </div>

      <ul style={{ listStyle: 'none', padding: 0, margin: '0 0 1rem' }}>
        {config.tracks.map((track) => (
          <li
            key={track.id}
            style={{
              display: 'flex',
              flexWrap: 'wrap',
              gap: '0.5rem',
              alignItems: 'center',
              padding: '0.5rem 0',
              borderBottom: '1px solid var(--fm-color-border)',
              opacity: track.enabled ? 1 : 0.55,
            }}
          >
            <span style={{ flex: '1 1 10rem', fontWeight: 600 }}>
              {track.title} · {track.artist} · {track.durationSec}s
            </span>
            <label style={{ display: 'inline-flex', gap: '0.35rem', alignItems: 'center', fontSize: '0.8125rem' }}>
              <input
                type="checkbox"
                checked={track.enabled}
                disabled={isSaving}
                onChange={() => {
                  void handleToggleEnabled(track);
                }}
              />
              {t('common.studio.jukebox.enabled')}
            </label>
            <Button
              label={t('common.studio.jukebox.delete')}
              variant="ghost"
              disabled={isSaving}
              onClick={() => {
                void handleDelete(track.id);
              }}
            />
          </li>
        ))}
      </ul>

      {config.tracks.length === 0 ? (
        <p style={{ color: 'var(--fm-color-text-muted)', fontSize: '0.875rem' }}>
          {t('common.studio.jukebox.empty')}
        </p>
      ) : null}

      <div style={{ display: 'grid', gap: '0.5rem', marginTop: '0.75rem' }}>
        <label>
          {t('common.studio.jukebox.trackTitle')}
          <input
            className="fm-input"
            value={draft.title}
            disabled={isSaving}
            onChange={(event) => {
              setDraft({ ...draft, title: event.target.value });
            }}
            style={{ display: 'block', width: '100%', marginTop: '0.25rem' }}
          />
        </label>
        <label>
          {t('common.studio.jukebox.artist')}
          <input
            className="fm-input"
            value={draft.artist}
            disabled={isSaving}
            onChange={(event) => {
              setDraft({ ...draft, artist: event.target.value });
            }}
            style={{ display: 'block', width: '100%', marginTop: '0.25rem' }}
          />
        </label>
        <label>
          {t('common.studio.jukebox.durationSec')}
          <input
            className="fm-input"
            type="number"
            min={1}
            value={draft.durationSec}
            disabled={isSaving}
            onChange={(event) => {
              setDraft({ ...draft, durationSec: event.target.value });
            }}
            style={{ display: 'block', width: '100%', marginTop: '0.25rem' }}
          />
        </label>
        <Button
          label={isSaving ? t('common.studio.jukebox.saving') : t('common.studio.jukebox.addTrack')}
          disabled={isSaving}
          onClick={() => {
            void handleAdd();
          }}
        />
      </div>
    </div>
  );
}
